import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { catchError, finalize, throwError } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { TOKEN } from '../keys/keys';
import { SpinnerService } from '../service/spinner.service';
import { SistemaContextService } from '../service/sistema-context.service';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const spinner = inject(SpinnerService);
  const sistemaContext = inject(SistemaContextService);
  // El spinner global se reserva para operaciones que mutan datos.
  // Las GET suelen dispararse en cascada para catálogos y recargas de tablas;
  // si también muestran spinner global, terminan tapando toasts y dejando la UI
  // innecesariamente bloqueada. Para casos puntuales, una GET puede forzar el
  // spinner enviando el header interno `x-show-spinner: true`.
  const method = req.method?.toUpperCase() ?? 'GET';
  const forceShowSpinner = req.headers.get('x-show-spinner') === 'true';
  const shouldShowSpinner = method !== 'GET' || forceShowSpinner;

  const sanitizedReq = req.headers.has('x-show-spinner')
    ? req.clone({ headers: req.headers.delete('x-show-spinner') })
    : req;

  if (shouldShowSpinner) {
    spinner.requestStarted();
  }

  // Inyección de dependencias (servicios necesarios en tiempo de ejecución)
  const cookie = inject(CookieService);
  const auth = inject(AuthService);

  // Lista explícita de rutas públicas que NO requieren token.
  // Aquí incluimos los endpoints de autenticación y otros recursos públicos
  // como los JSON de traducción (`/i18n/*.json`) para que el loader pueda
  // descargarlos sin enviar Authorization.
  const urlLower = (sanitizedReq.url || '').toLowerCase();
  const publicPaths = [
    '/api/v1/auth/login',
    '/api/v1/auth/register',
    '/api/v1/auth/forgot-password',
    '/api/v1/auth/check',
    '/v1/auth/login',
    '/v1/auth/register',
    '/v1/auth/forgot-password',
    '/v1/auth/check',
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/check',
    '/i18n',
    '/i18n/',
  ];

  // Solo las rutas públicas (ej. login, register, check, traducciones) pueden
  // omitir la verificación del token. NO reutilizar `excludedGetSpinnerPatterns`
  // aquí: esa lista solo controla el comportamiento del spinner.
  const isPublicEndpoint = publicPaths.some((p) => urlLower.includes(p.toLowerCase()));

  if (isPublicEndpoint) {
    // Permitir la llamada sin token (login/register/check, archivos de i18n, etc.)
    return next(sanitizedReq).pipe(
      catchError((err) => {
        if (shouldShowSpinner) spinner.requestFinished();
        const LOGOUT_CODES = [401];
        if (err instanceof HttpErrorResponse ? LOGOUT_CODES.includes(err.status) : false)
          return restrict(auth, 'No autorizado', true);
        else return restrict(auth, err, false);
      }),
      finalize(() => {
        if (shouldShowSpinner) spinner.requestFinished();
      })
    );
  }

  // Obtenemos el token de las cookies (si existe)
  const token = cookie.get(TOKEN);
  // Si no hay token disponible, bloqueamos el acceso y forzamos logout
  if (!token) {
    if (shouldShowSpinner) spinner.requestFinished();
    return restrict(auth, 'No se proveyó el TOKEN', true);
  }
  // Clonamos la solicitud para agregar el encabezado Authorization y cabeceras extra
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
  };

  if (sistemaContext.empresaActual) {
    headers['x-empresa-id'] = sistemaContext.empresaActual;
  }

  const request = req.clone({
    // Usamos la request saneada para no filtrar headers internos al backend.
    ...sanitizedReq,
    setHeaders: headers,
  });

  return next(request).pipe(
    catchError((err) => {
      if (shouldShowSpinner) spinner.requestFinished();
      // Solo forzar logout cuando recibimos 401 (unauthenticated).
      // Mantener 403 (forbidden) como un error recuperable para que la UI pueda
      // mostrar un mensaje apropiado sin desconectar al usuario.
      const LOGOUT_CODES = [401];
      if (err instanceof HttpErrorResponse ? LOGOUT_CODES.includes(err.status) : false)
        return restrict(auth, 'No autorizado', true);
      else return restrict(auth, err, false);
    }),
    finalize(() => {
      if (shouldShowSpinner) spinner.requestFinished();
    })
  );
};

/**
 * @description
 * Realiza el logout, lanza un error con un mensaje personalizado y detiene el flujo.
 */
const restrict = (auth: AuthService, msg: any, logout: boolean) => {
  if (logout) auth.logout();
  return throwError(() => msg);
};
